import { useEffect, useRef, useState } from "react";
import { Phone, Mail, ArrowRight, Check, Star, MapPin, Hammer, LayoutGrid, ArrowUpRight, ChevronDown, X } from "lucide-react";
import { toast } from "sonner";
import { api, getUtm, BUSINESS } from "@/lib/sleek";
import { fireConversion } from "@/lib/gtag";
import ExitIntentPopup from "@/components/ExitIntentPopup";

const INTERESTS = ["Cabinetry & Design", "Full-Service Remodeling", "Both", "Not sure yet — let's talk"];

const AB_HERO_B = { h1: "Get The Kitchen You Crave!", h2: "" };

function getAbVariant() {
  let v = localStorage.getItem("sleek_ab_hero");
  if (v !== "A" && v !== "B") {
    v = Math.random() < 0.5 ? "A" : "B";
    localStorage.setItem("sleek_ab_hero", v);
  }
  return v;
}

const DEFAULT_PHOTOS = {
  hero: "/photos/blumwayland/chef_hero_hires.webp",
  offer_cabinets: "/photos/wayland_kitchen/island_range_wide.webp",
  offer_renovation: "/photos/concord_kitchen_more/za1_island_ovens_view.webp",
  gallery: [
    "/photos/wayland_makeover/01_pantry_wall_kitchen.webp",
    "/photos/concord_kitchen_more/z9_kitchen_hood_wide.webp",
    "/photos/skyhigh/angle_ec_craft_detail.jpg",
    "/photos/wayland_kitchen/island_marble_living.webp",
    "/photos/dedham_kitchen/01_sink_range_run.webp",
    "/photos/blumwayland/kitchen/hero_thermador.jpg",
  ],
  hero_headline_1: "Get The Kitchen You Crave!",
  hero_headline_2: "",
  hero_sub: "From cabinetry to full remodels — serving MetroWest homeowners for 27+ years!",
  cab_title: "Cabinets & Countertops",
  cab_desc: "New cabinetry and stone can completely change a kitchen — without gutting the room. Choose from Candlelight and Showplace cabinet lines with countless finishes, topped with stone fabricated by our partners at Dream Stoneworks.",
  ren_title: "Full Kitchen Renovation",
  ren_desc: "Walls, layout, lighting, flooring, cabinetry, stone — a complete transformation managed end-to-end by our own project managers and finish carpenters. One team from design through the final walkthrough.",
};

const STEPS = [
  { n: "01", t: "Start in the showroom", d: "A private, one-on-one visit in Northborough. See real cabinetry, stone and finishes up close — and take all the time you need." },
  { n: "02", t: "Meet with our designer", d: "We'll listen and gather every detail of how you live — then take the time to thoughtfully craft a design that gets it right." },
  { n: "03", t: "Love the result", d: "From cabinetry to the full remodel, we sweat every detail so the finished space looks even better than the drawings." },
];

function useReveal() {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") { setShown(true); return; }
    const ob = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setShown(true); ob.disconnect(); } }, { threshold: 0.12 });
    ob.observe(el);
    return () => ob.disconnect();
  }, []);
  return [ref, shown];
}

function AccordionField({ label, required, value, onChange, defaultOpen = false, type = "text", testId, fieldClassName, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-zinc-200" data-testid={`${testId}-accordion`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left"
        data-testid={`${testId}-toggle`}
      >
        <span className="text-sm text-zinc-800 truncate min-w-0">
          {label}
          {required && <span className="text-[#FF001E] ml-1 text-xs" aria-hidden="true">★</span>}
          {!open && value && <span className="text-zinc-400 text-xs ml-2 truncate">— {value}</span>}
        </span>
        <ChevronDown size={16} className={`text-zinc-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-4">
          {children ? children : (
            <input aria-label={label} className={fieldClassName} type={type} value={value} onChange={onChange} required={required} data-testid={testId} autoFocus />
          )}
        </div>
      )}
    </div>
  );
}

function Reveal({ children, delay = 0, className = "", eager = false }) {
  const [ref, observedShown] = useReveal();
  const shown = eager || observedShown;
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: shown ? 1 : 0,
        transform: shown ? "translateY(0)" : "translateY(24px)",
        transition: `opacity .7s ease ${delay}ms, transform .7s ease ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

export default function Landing() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", interest: INTERESTS[0], website: "" });
  const [abVariant] = useState(getAbVariant);

  useEffect(() => {
    if (!localStorage.getItem("sleek_ab_hero_logged")) {
      api.post("/landing/ab-view", { variant: abVariant })
        .then(() => localStorage.setItem("sleek_ab_hero_logged", "1"))
        .catch(() => {});
    }
  }, [abVariant]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [photos, setPhotos] = useState(DEFAULT_PHOTOS);
  const [trackingPhone, setTrackingPhone] = useState({ number: BUSINESS.phone, tel_href: BUSINESS.phoneHref });
  const formRef = useRef(null);
  const formCardRef = useRef(null);
  const [showFloatingCta, setShowFloatingCta] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => { if (e.key === "Escape") setLightbox(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox]);

  useEffect(() => {
    const el = formCardRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const ob = new IntersectionObserver(([entry]) => setShowFloatingCta(!entry.isIntersecting), { threshold: 0 });
    ob.observe(el);
    return () => ob.disconnect();
  }, []);

  useEffect(() => { document.title = "Kitchen Cabinets & Renovations | Sleek Renovations — Northborough, MA"; }, []);
  useEffect(() => { api.get("/landing-config").then((r) => setPhotos(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    api.get("/call-tracking-number").then((r) => {
      if (r.data.configured) setTrackingPhone({ number: r.data.number, tel_href: r.data.tel_href });
    }).catch(() => {});
  }, []);

  const goToForm = (interest) => {
    if (interest) setForm((f) => ({ ...f, interest }));
    (formCardRef.current || formRef.current)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { toast.error("Please add your name and email."); return; }
    setSubmitting(true);
    try {
      await api.post("/leads", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        project_type: form.interest,
        website: form.website,
        source: "landing",
        ab_variant: abVariant,
        ...getUtm(),
      });
      fireConversion("lead");
      setDone(true);
    } catch {
      toast.error("Something went wrong — please try again or call us.");
    } finally {
      setSubmitting(false);
    }
  };

  const field = "w-full rounded-none border border-zinc-200 bg-white px-4 py-3.5 text-sm focus:outline-none focus:border-[#FF001E] focus:ring-1 focus:ring-[#FF001E] transition-colors";
  const darkField = "w-full rounded-none border border-zinc-300 bg-white text-zinc-950 placeholder:text-zinc-400 px-4 py-3.5 text-sm focus:outline-none focus:border-[#FF001E] focus:ring-1 focus:ring-[#FF001E] transition-colors";

  return (
    <div className="bg-white text-zinc-950 min-h-screen" data-testid="landing-page">
      <ExitIntentPopup abVariant={abVariant} interest={form.interest} suppress={done} />
      {/* Top thin bar — matches main site */}
      <div className="hidden md:block border-b border-zinc-200 text-xs bg-white">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-2 flex justify-between items-center text-zinc-600">
          <a href={BUSINESS.mapsHref} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-zinc-900 transition-colors" data-testid="landing-topbar-directions">
            <MapPin size={12} /> {BUSINESS.address}
          </a>
          <div className="flex items-center gap-6">
            <a href={BUSINESS.emailHref} className="flex items-center gap-2 hover:text-zinc-900" data-testid="landing-topbar-email">
              <Mail size={12} /> {BUSINESS.email}
            </a>
          </div>
        </div>
      </div>

      {/* Minimal header — logo + call only */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-zinc-200/70">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 h-20 flex items-center justify-between">
          <div className="font-display flex items-center gap-2.5" data-testid="landing-logo">
            <img
              src="/brand/logo.png"
              alt="Sleek Renovations"
              className="h-12 md:h-14 w-auto select-none"
              draggable="false"
              style={{ filter: "brightness(0) saturate(100%) invert(13%) sepia(97%) saturate(6739%) hue-rotate(347deg) brightness(94%) contrast(114%)" }}
            />
            <div className="flex flex-col leading-none pt-1.5 md:pt-2">
              <div className="text-xl tracking-tight whitespace-nowrap">
                <span className="font-light">SLEEK</span>
                <span className="font-medium ml-1">KITCHEN &amp; BATH</span>
              </div>
              <div className="hidden md:block text-[10px] tracking-[0.3em] text-zinc-500 uppercase mt-1.5 whitespace-nowrap">
                by Sleek Renovations
              </div>
            </div>
          </div>
          <a href={trackingPhone.tel_href} className="inline-flex items-center gap-1.5 sm:gap-2 border-2 border-zinc-950 px-3 py-1.5 sm:px-4 sm:py-2 text-[10px] sm:text-sm font-semibold uppercase tracking-wider whitespace-nowrap hover:bg-zinc-950 hover:text-white transition-colors" data-testid="landing-header-phone">
            <Phone size={13} className="shrink-0" /> Call Now
          </a>
        </div>
      </header>

      {/* HERO + LEAD FORM — combined above the fold so the offer is visible without scrolling.
          On mobile the photo is a fixed-height band ABOVE the form (not a full-bleed background,
          which was cropping/zooming too much on tall narrow screens); on lg+ it's the full-bleed background as before. */}
      <section ref={formRef} id="get-started" className="relative overflow-hidden scroll-mt-16 bg-zinc-950">
        <div className="relative h-72 sm:h-80 lg:absolute lg:inset-0 lg:h-auto">
          <img src={photos.hero} alt="Custom kitchen remodel by Sleek Renovations" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/0 via-black/5 to-zinc-950/55 lg:bg-gradient-to-r lg:from-black/60 lg:via-black/45 lg:to-black/35" />
        </div>
        <div className="relative mx-auto max-w-[1300px] px-6 md:px-10 pt-3 sm:pt-4 md:pt-6 pb-0 lg:py-16">
          <div className="grid lg:grid-cols-12 gap-6 sm:gap-8 lg:gap-8 items-center">
            {/* Left — headline (H1 shown as an overlay on the mobile photo band above; shown normally here on lg+) */}
            <div className="order-2 lg:order-1 lg:col-span-6 xl:col-span-7 mt-2 pt-2 pb-8 sm:pb-10 -mx-6 px-6 md:-mx-10 md:px-10 bg-black lg:bg-transparent lg:mx-0 lg:px-0 lg:pb-0 border-t border-white/10 lg:mt-0 lg:pt-0 lg:border-0 min-w-0">
              <Reveal eager>
                <div className="hidden lg:block text-[11px] tracking-[0.3em] uppercase text-white/70">Sleek Kitchen &amp; Bath Showroom · Northborough, MA</div>
              </Reveal>
              <Reveal eager delay={120}>
                <h1 className="hidden lg:block font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-white mt-5 leading-[1.05]">
                  {abVariant === "B" ? AB_HERO_B.h1 : photos.hero_headline_1}
                  {(abVariant === "B" ? AB_HERO_B.h2 : photos.hero_headline_2) && (
                    <>
                      <br />
                      <span className="text-white/90">{abVariant === "B" ? AB_HERO_B.h2 : photos.hero_headline_2}</span>
                    </>
                  )}
                </h1>
              </Reveal>
              <Reveal eager delay={240}>
                <p className="text-white/80 text-lg md:text-xl mt-0 lg:mt-6 max-w-xl leading-relaxed">
                  {photos.hero_sub}
                </p>
              </Reveal>
              <Reveal eager delay={360}>
                <div className="mt-8 flex flex-wrap gap-3">
                  <a href={trackingPhone.tel_href} className="inline-flex items-center gap-2 border border-white text-white px-7 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-white hover:text-zinc-950 transition-colors" data-testid="hero-cta-call">
                    <Phone size={15} /> Call {trackingPhone.number}
                  </a>
                </div>
              </Reveal>
            </div>

            {/* Right — lead form, right next to the headline on desktop; first on mobile (right after the photo) */}
            <div ref={formCardRef} className="order-1 lg:order-2 lg:col-span-6 xl:col-span-5 scroll-mt-24 min-w-0">
              <Reveal eager delay={200}>
                {done ? (
                  <div className="bg-white border border-zinc-200 p-6 sm:p-8 md:p-10 shadow-2xl" data-testid="landing-form-success">
                    <div className="font-display text-xl sm:text-2xl md:text-3xl font-light text-zinc-950">You're in great hands.</div>
                    <p className="text-zinc-600 mt-4 leading-relaxed text-sm md:text-base">
                      Thanks — we've got your request and will reach out shortly to set up your free consultation.
                      Want to talk sooner? Call us at <a href={trackingPhone.tel_href} className="underline">{trackingPhone.number}</a>.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="bg-black py-2 sm:py-2.5 mb-4 sm:mb-5 lg:hidden">
                      <h1 className="font-display text-2xl sm:text-3xl md:text-4xl font-light tracking-tight text-white text-center leading-[1.1]">
                        {abVariant === "B" ? AB_HERO_B.h1 : photos.hero_headline_1}
                        {(abVariant === "B" ? AB_HERO_B.h2 : photos.hero_headline_2) && (
                          <>
                            <br />
                            <span className="text-white/90">{abVariant === "B" ? AB_HERO_B.h2 : photos.hero_headline_2}</span>
                          </>
                        )}
                      </h1>
                    </div>
                    <form onSubmit={submit} className="bg-white border border-zinc-200 p-5 sm:p-6 md:p-8 shadow-2xl" data-testid="landing-lead-form">
                      <div className="flex items-center justify-between gap-3">
                        <div className="font-display text-lg sm:text-xl md:text-2xl font-light text-zinc-950">Ready to begin?</div>
                        <button type="submit" disabled={submitting} className="shrink-0 bg-[#FF001E] text-white px-5 py-2.5 sm:px-6 sm:py-3 text-xs sm:text-sm font-semibold uppercase tracking-wider hover:bg-[#D90019] transition-colors disabled:opacity-60" data-testid="landing-form-submit">
                          {submitting ? "Sending…" : "Book Now"}
                        </button>
                      </div>
                      <div className="mt-3 sm:mt-4 space-y-2">
                      <AccordionField
                        label="Your name"
                        required
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        fieldClassName={darkField}
                        testId="landing-input-name"
                      />
                      <input type="text" name="website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} tabIndex={-1} autoComplete="off" aria-hidden="true" style={{ position: "absolute", left: "-9999px", height: 0, width: 0, opacity: 0 }} data-testid="landing-honeypot" />
                      <AccordionField
                        label="Email address"
                        required
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        fieldClassName={darkField}
                        testId="landing-input-email"
                      />
                      <AccordionField
                        label="Phone number"
                        type="tel"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        fieldClassName={darkField}
                        testId="landing-input-phone"
                      />
                      <AccordionField
                        label="What are you interested in?"
                        value={form.interest}
                        testId="landing-interest-field"
                      >
                        <div className="flex flex-wrap gap-2">
                          {INTERESTS.map((opt) => (
                            <button
                              type="button"
                              key={opt}
                              onClick={() => setForm({ ...form, interest: opt })}
                              className={`px-3 py-2 sm:px-3.5 sm:py-2.5 border text-xs sm:text-sm transition-colors ${form.interest === opt ? "bg-[#FF001E] border-[#FF001E] text-white" : "border-zinc-300 text-zinc-700 hover:border-zinc-950"}`}
                              data-testid={`landing-interest-${opt === INTERESTS[0] ? "cabinets" : opt === INTERESTS[1] ? "renovation" : opt === INTERESTS[2] ? "both" : "unsure"}`}
                            >
                              {opt}
                            </button>
                          ))}
                        </div>
                      </AccordionField>
                    </div>
                      <div className="text-xs sm:text-sm tracking-[0.06em] sm:tracking-[0.3em] uppercase text-[#FF001E] font-semibold text-center mt-4" data-testid="landing-form-eyebrow">Complimentary design discovery consultation</div>
                    </form>
                  </>
                )}
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST LINE — white space directly beneath the booking box */}
      <div className="bg-white" data-testid="landing-trust-line">
        <div className="mx-auto max-w-[1200px] px-6 md:px-10 py-6 flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-zinc-500 text-xs tracking-[0.15em] uppercase text-center">
          <span>Family-Owned &amp; Operated</span>
          <span className="text-black text-3xl leading-none">·</span>
          <span>4.5★ Owner Rating</span>
        </div>
      </div>

      {/* DUAL OFFER — equal weight, no forced bundling */}
      <section className="border-b border-zinc-200 scroll-mt-16" id="paths">
        <div className="mx-auto max-w-[1200px] px-6 md:px-10 py-20 md:py-24">
          <Reveal>
            <div className="text-xs tracking-[0.3em] uppercase text-zinc-500">Choose the level of service that's right for you</div>
            <h2 className="font-display text-4xl md:text-5xl font-light tracking-tight mt-4 max-w-2xl">Work with us your way.</h2>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-2 gap-6">
            <Reveal delay={100}>
              <div className="border border-zinc-200 group h-full flex flex-col" data-testid="offer-card-cabinets">
                <div className="relative overflow-hidden aspect-[16/10]">
                  <img src={photos.offer_cabinets} alt="Custom cabinetry and countertops" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
                  {(photos.offer_cabinets_label || "") && (
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                      <div className="px-4 py-3 text-white text-xs font-semibold tracking-[0.2em] uppercase" data-testid="offer-cabinets-label">{photos.offer_cabinets_label}</div>
                    </div>
                  )}
                </div>
                <div className="p-8 md:p-10 flex flex-col flex-1">
                  <div className="flex items-center gap-3 text-xs tracking-[0.25em] uppercase text-zinc-500"><LayoutGrid size={14} strokeWidth={1.5} /> Fresh New Look</div>
                  <h3 className="font-display text-2xl md:text-3xl font-light tracking-tight mt-3">{photos.cab_title}</h3>
                  <p className="text-zinc-600 mt-4 leading-relaxed text-sm md:text-base">
                    {photos.cab_desc}
                  </p>
                  <ul className="mt-6 space-y-2.5 text-sm text-zinc-700">
                    {["Semi-custom & fully custom cabinetry from premium brands", "Countertops and hardware", "Professional design services"].map((b) => (
                      <li key={b} className="flex gap-2.5"><Check size={15} className="mt-0.5 text-[#FF001E] shrink-0" strokeWidth={2} /> {b}</li>
                    ))}
                  </ul>
                  <button onClick={() => goToForm(INTERESTS[0])} className="mt-8 inline-flex items-center justify-center gap-2 bg-[#FF001E] text-white px-7 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-[#D90019] transition-colors w-full" data-testid="offer-cta-cabinets">
                    Explore Cabinetry <ArrowRight size={15} />
                  </button>
                </div>
              </div>
            </Reveal>
            <Reveal delay={220}>
              <div className="border border-zinc-200 group h-full flex flex-col" data-testid="offer-card-renovation">
                <div className="relative overflow-hidden aspect-[16/10]">
                  <img src={photos.offer_renovation} alt="Full kitchen renovation" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
                  {(photos.offer_renovation_label || "") && (
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                      <div className="px-4 py-3 text-white text-xs font-semibold tracking-[0.2em] uppercase" data-testid="offer-renovation-label">{photos.offer_renovation_label}</div>
                    </div>
                  )}
                </div>
                <div className="p-8 md:p-10 flex flex-col flex-1">
                  <div className="flex items-center gap-3 text-xs tracking-[0.25em] uppercase text-zinc-500"><Hammer size={14} strokeWidth={1.5} /> Transform</div>
                  <h3 className="font-display text-2xl md:text-3xl font-light tracking-tight mt-3">{photos.ren_title}</h3>
                  <p className="text-zinc-600 mt-4 leading-relaxed text-sm md:text-base">
                    {photos.ren_desc}
                  </p>
                  <ul className="mt-6 space-y-2.5 text-sm text-zinc-700">
                    {["Kitchens, baths & whole-home renovations", "Design through construction management", "Craftsmanship we'd put our own name on — because we do"].map((b) => (
                      <li key={b} className="flex gap-2.5"><Check size={15} className="mt-0.5 text-[#FF001E] shrink-0" strokeWidth={2} /> {b}</li>
                    ))}
                  </ul>
                  <button onClick={() => goToForm(INTERESTS[1])} className="mt-8 inline-flex items-center justify-center gap-2 bg-zinc-950 text-white px-7 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-[#FF001E] transition-colors w-full" data-testid="offer-cta-renovation">
                    Explore Remodeling <ArrowRight size={15} />
                  </button>
                </div>
              </div>
            </Reveal>
          </div>
          <div className="mt-10 grid md:grid-cols-2 gap-6 text-sm md:text-base text-zinc-700" data-testid="friction-removers">
            <div className="border-l-2 border-[#FF001E] pl-5 leading-relaxed">
              <span className="font-medium text-zinc-950">Already have a contractor?</span> We'd be happy to supply
              cabinetry from our premium vendor lines, plus design, countertops, and material selections.
            </div>
            <div className="border-l-2 border-zinc-950 pl-5 leading-relaxed">
              <span className="font-medium text-zinc-950">Prefer one company to handle everything?</span> Our remodeling
              team can manage your project from concept through completion.
            </div>
          </div>
        </div>
      </section>

      {/* PROOF — real local projects */}
      <section className="border-b border-zinc-200 bg-zinc-50">
        <div className="mx-auto max-w-[1200px] px-6 md:px-10 py-20 md:py-24">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <div className="text-xs tracking-[0.3em] uppercase text-zinc-500">Featured projects</div>
                <h2 className="font-display text-4xl md:text-5xl font-light tracking-tight mt-4">Kitchens that speak for themselves.</h2>
              </div>
              <a href="/portfolio" className="inline-flex items-center gap-1.5 text-xs tracking-[0.2em] uppercase border-b border-zinc-950 pb-1 hover:gap-3 transition-all" data-testid="landing-portfolio-link">
                Browse the full portfolio <ArrowUpRight size={13} />
              </a>
            </div>
          </Reveal>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-3">
            {photos.gallery.map((src, i) => (
              <Reveal key={`${src}-${i}`} delay={i * 80}>
                <button
                  type="button"
                  onClick={() => setLightbox({ src, label: (photos.gallery_labels || [])[i] })}
                  className="relative overflow-hidden group aspect-[4/3] w-full text-left cursor-zoom-in"
                  data-testid={`landing-gallery-${i}`}
                >
                  <img src={src} alt={`Kitchen remodel by Sleek Renovations — ${(photos.gallery_labels || [])[i] || `project photo ${i + 1}`}`} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" />
                  {(photos.gallery_labels || [])[i] && (
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                      <div className="px-4 py-3 text-white text-xs font-semibold tracking-[0.2em] uppercase" data-testid={`landing-gallery-label-${i}`}>
                        {photos.gallery_labels[i]}
                      </div>
                    </div>
                  )}
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY LIGHTBOX */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4 sm:p-8"
          onClick={() => setLightbox(null)}
          data-testid="landing-gallery-lightbox"
        >
          <button
            type="button"
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 sm:top-6 sm:right-6 text-white/80 hover:text-white transition-colors z-10"
            data-testid="landing-lightbox-close"
          >
            <X size={28} />
          </button>
          <img
            src={lightbox.src}
            alt={lightbox.label || "Kitchen remodel by Sleek Renovations"}
            onClick={(e) => e.stopPropagation()}
            className="max-w-full max-h-full lg:max-w-[80vw] lg:max-h-[80vh] w-auto h-auto object-contain shadow-2xl"
          />
          {lightbox.label && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white text-xs font-semibold tracking-[0.2em] uppercase" data-testid="landing-lightbox-label">
              {lightbox.label}
            </div>
          )}
        </div>
      )}

      {/* TESTIMONIALS */}
      <section className="border-b border-zinc-200">
        <div className="mx-auto max-w-[1200px] px-6 md:px-10 py-20 md:py-24 grid md:grid-cols-3 gap-6">
          <Reveal>
            <div className="border border-zinc-200 p-8 md:p-10 h-full">
              <div className="flex gap-1 text-[#FF001E]">{[...Array(5)].map((_, i) => <Star key={i} size={14} className="fill-current" />)}</div>
              <p className="text-zinc-700 leading-relaxed mt-5 text-sm md:text-base">
                "Chris and his workers are honest and trustworthy, and personally committed to delivering excellent
                work. I feel confident that work will always be done to the highest standard."
              </p>
              <div className="mt-6 text-xs tracking-[0.2em] uppercase text-zinc-500">Carl B. · Wayland, MA</div>
            </div>
          </Reveal>
          <Reveal delay={150}>
            <div className="border border-zinc-200 p-8 md:p-10 h-full" data-testid="landing-testimonial-jennifer">
              <div className="flex gap-1 text-[#FF001E]">{[...Array(5)].map((_, i) => <Star key={i} size={14} className="fill-current" />)}</div>
              <p className="text-zinc-700 leading-relaxed mt-5 text-sm md:text-base">
                "I would highly recommend Sleek Renovation — Chris and his crew. The work is impeccable, to perfection
                and beautiful... Chris and crew are all very nice and helpful."
              </p>
              <div className="mt-6 text-xs tracking-[0.2em] uppercase text-zinc-500">Jennifer · Concord, MA</div>
            </div>
          </Reveal>
          <Reveal delay={300}>
            <div className="border border-zinc-200 p-8 md:p-10 h-full">
              <div className="flex gap-1 text-[#FF001E]">{[...Array(5)].map((_, i) => <Star key={i} size={14} className="fill-current" />)}</div>
              <p className="text-zinc-700 leading-relaxed mt-5 text-sm md:text-base">
                "From the first showroom visit to the final punch list, Sleek made a major remodel feel effortless.
                The cabinetry detailing is best-in-class."
              </p>
              <div className="mt-6 text-xs tracking-[0.2em] uppercase text-zinc-500">Marta R. · Revere, MA</div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PROCESS */}
      <section className="border-b border-zinc-200">
        <div className="mx-auto max-w-[1200px] px-6 md:px-10 py-20 md:py-24">
          <Reveal>
            <div className="text-xs tracking-[0.3em] uppercase text-zinc-500">The design process</div>
            <h2 className="font-display text-4xl md:text-5xl font-light tracking-tight mt-4">Three steps. Zero pressure.</h2>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-3 md:divide-x divide-zinc-200 border-t border-zinc-200">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 120}>
                <div className={`pt-8 pb-8 md:pr-8 border-b md:border-b-0 border-zinc-200 h-full ${i === 0 ? "md:pl-0" : "md:pl-8"}`}>
                  <div className="text-xs tracking-[0.3em] text-[#FF001E] font-semibold">{s.n}</div>
                  <h3 className="font-display text-xl md:text-2xl font-light tracking-tight mt-3">{s.t}</h3>
                  <p className="text-zinc-600 text-sm md:text-base leading-relaxed mt-3">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER — minimal */}
      <footer className="border-t border-zinc-200">
        <div className="mx-auto max-w-[1200px] px-6 md:px-10 py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-zinc-500">
          <div>© {new Date().getFullYear()} {BUSINESS.remodel} · {BUSINESS.showroom} · {BUSINESS.address}</div>
          <div className="flex flex-col gap-1">
            <a href={trackingPhone.tel_href} className="text-[#FF001E] font-semibold hover:text-[#D90019] transition-colors" data-testid="landing-footer-phone">{trackingPhone.number}</a>
            <a href={BUSINESS.emailHref} className="text-[#FF001E] font-semibold hover:text-[#D90019] transition-colors" data-testid="landing-footer-email">{BUSINESS.email}</a>
            <a href="https://www.sleekrenovations.com" className="text-[#FF001E] font-semibold hover:text-[#D90019] transition-colors" data-testid="landing-footer-site-link">www.sleekrenovations.com</a>
          </div>
        </div>
      </footer>

      {/* FLOATING BOOK NOW — appears once the booking box scrolls out of view */}
      <button
        type="button"
        onClick={() => goToForm()}
        className={`fixed bottom-5 right-5 z-50 bg-[#FF001E] text-white px-6 py-3.5 text-sm font-semibold uppercase tracking-wider shadow-2xl hover:bg-[#D90019] transition-all duration-300 ${showFloatingCta ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-4 pointer-events-none"}`}
        data-testid="landing-floating-book-now"
      >
        Book Now
      </button>
    </div>
  );
}
